import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Search, LogOut, User, BookMarked, TrendingUp, Crown, Users, Activity, BarChart3, Camera, Rss, Menu, X, Wallet, Mail, AlertCircle, Megaphone } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CurrencySwitcher from "@/components/CurrencySwitcher";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const linkCls = ({ isActive }) =>
    `text-sm font-semibold transition-colors px-1 py-1 ${
      isActive ? "text-[#E11D48]" : "text-slate-600 hover:text-slate-900"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center gap-4 lg:gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <div className="relative w-9 h-9 rounded-full bg-[#E11D48] flex items-center justify-center shadow-sm overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1/2 bg-[#E11D48]" />
            <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-white" />
            <div className="absolute top-1/2 left-0 right-0 h-[3px] bg-slate-900 -translate-y-1/2" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white border-[3px] border-slate-900" />
          </div>
          <div className="leading-none">
            <div className="font-heading font-extrabold text-lg tracking-tight text-slate-900">Cartonomie</div>
            <div className="text-[10px] text-slate-400 -mt-0.5 font-medium tracking-wide">Global price index</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-4 lg:gap-5">
          <NavLink to="/sets" className={linkCls} data-testid="nav-sets">{t("nav.sets")}</NavLink>
          <NavLink to="/search" className={linkCls} data-testid="nav-search">{t("nav.cards")}</NavLink>
          <NavLink to="/rankings" className={linkCls} data-testid="nav-rankings">
            <span className="inline-flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> {t("nav.rankings")}</span>
          </NavLink>
          <NavLink to="/index" className={linkCls} data-testid="nav-index">
            <span className="inline-flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Index</span>
          </NavLink>
          <NavLink to="/feed" className={linkCls} data-testid="nav-feed">
            <span className="inline-flex items-center gap-1"><Rss className="w-3.5 h-3.5" /> {t("nav.feed", "Feed")}</span>
          </NavLink>
          <NavLink to="/sponsors" className={linkCls} data-testid="nav-sponsors">
            <span className="inline-flex items-center gap-1"><Megaphone className="w-3.5 h-3.5" /> Annonces</span>
          </NavLink>
          <NavLink to="/pricing" className={linkCls} data-testid="nav-pricing">
            <span className="inline-flex items-center gap-1"><Crown className="w-3.5 h-3.5 text-[#F59E0B]" /> {t("nav.premium")}</span>
          </NavLink>
          {user && user.email && (
            <>
              <NavLink to="/scan" className={linkCls} data-testid="nav-scan">
                <span className="inline-flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> {t("nav.scan", "Scan")}</span>
              </NavLink>
              <NavLink to="/collection" className={linkCls} data-testid="nav-collection">{t("nav.collection")}</NavLink>
              <NavLink to="/portfolio" className={linkCls} data-testid="nav-portfolio">
                <span className="inline-flex items-center gap-1"><Wallet className="w-3.5 h-3.5 text-[#E11D48]" /> {t("nav.portfolio", "Portfolio")}</span>
              </NavLink>
              {user.role === "admin" && (
                <>
                  <NavLink to="/admin/analytics" className={linkCls} data-testid="nav-admin-analytics">
                    <span className="inline-flex items-center gap-1 text-[#2563EB]"><BarChart3 className="w-3.5 h-3.5" /> Analytics</span>
                  </NavLink>
                  <NavLink to="/admin/contact" className={linkCls} data-testid="nav-admin-contact">
                    <span className="inline-flex items-center gap-1 text-[#2563EB]"><Mail className="w-3.5 h-3.5" /> Inbox</span>
                  </NavLink>
                  <NavLink to="/admin/price-audit" className={linkCls} data-testid="nav-admin-price-audit">
                    <span className="inline-flex items-center gap-1 text-[#2563EB]"><AlertCircle className="w-3.5 h-3.5" /> Audit Prix</span>
                  </NavLink>
                </>
              )}
            </>
          )}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden lg:flex items-center w-60 relative" data-testid="navbar-search-form">
          <Search className="w-4 h-4 absolute left-3 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Charizard, Pikachu…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="navbar-search-input"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-xl"
          />
        </form>

        <div className="ml-auto lg:ml-0 flex items-center gap-2">
          <div className="hidden md:block">
            <CurrencySwitcher />
          </div>
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>
          {user && user.email ? (
            <>
              <Link to="/account" data-testid="navbar-collection-btn" className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl transition ${user.is_premium ? "bg-amber-50 border border-amber-200 text-amber-700" : "border border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                {user.is_premium ? <Crown className="w-3.5 h-3.5" /> : <BookMarked className="w-3.5 h-3.5" />}
                <span className="max-w-[120px] truncate">{user.name || user.email.split("@")[0]}</span>
                {user.is_premium && <span className="font-bold">·PRO</span>}
              </Link>
              <button onClick={logout} data-testid="navbar-logout-btn" className="p-2 text-slate-500 hover:text-[#E11D48] transition rounded-xl hover:bg-slate-100" title={t("nav.logout")}>
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="navbar-login-btn"
                className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold rounded-xl border border-slate-300 text-slate-700 hover:border-[#E11D48] hover:text-[#E11D48] px-3 py-1.5 sm:px-4 sm:py-2 transition whitespace-nowrap"
              >
                <LogOut className="w-3.5 h-3.5 rotate-180" />
                {t("nav.login")}
              </Link>
              <Link to="/register" data-testid="navbar-register-btn" className="btn-primary text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4">
                <User className="w-4 h-4 inline mr-1 -mt-0.5" /> {t("nav.signup")}
              </Link>
            </>
          )}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden p-2 rounded-xl hover:bg-slate-100"
            aria-label="menu"
            data-testid="navbar-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="md:hidden border-t border-slate-200 bg-white px-4 py-3 flex flex-col gap-1" data-testid="navbar-mobile-menu">
          <NavLink to="/sets" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-slate-700">{t("nav.sets")}</NavLink>
          <NavLink to="/search" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-slate-700">{t("nav.cards")}</NavLink>
          <NavLink to="/rankings" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-slate-700">{t("nav.rankings")}</NavLink>
          <NavLink to="/feed" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-slate-700">{t("nav.feed", "Feed")}</NavLink>
          <NavLink to="/sponsors" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-slate-700">Annonces</NavLink>
          <NavLink to="/pricing" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-amber-700">{t("nav.premium")}</NavLink>
          {user && user.email ? (
            <>
              <NavLink to="/scan" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-slate-700">{t("nav.scan", "Scan")}</NavLink>
              <NavLink to="/collection" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-slate-700">{t("nav.collection")}</NavLink>
              <NavLink to="/portfolio" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-slate-700">{t("nav.portfolio", "Portfolio")}</NavLink>
              <NavLink to="/account" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-slate-700">{t("nav.account", "Account")}</NavLink>
              {user.role === "admin" && (
                <>
                  <div className="mt-2 pt-2 border-t border-slate-100 px-3 text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                    Admin
                  </div>
                  <NavLink
                    to="/admin/analytics"
                    onClick={() => setMobileOpen(false)}
                    data-testid="navbar-mobile-admin-analytics"
                    className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-[#2563EB] inline-flex items-center gap-2"
                  >
                    <BarChart3 className="w-4 h-4" /> Analytics
                  </NavLink>
                  <NavLink
                    to="/admin/contact"
                    onClick={() => setMobileOpen(false)}
                    data-testid="navbar-mobile-admin-contact"
                    className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-[#2563EB] inline-flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" /> Inbox
                  </NavLink>
                  <NavLink
                    to="/admin/price-audit"
                    onClick={() => setMobileOpen(false)}
                    data-testid="navbar-mobile-admin-price-audit"
                    className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-[#2563EB] inline-flex items-center gap-2"
                  >
                    <AlertCircle className="w-4 h-4" /> Audit Prix
                  </NavLink>
                </>
              )}
              <button onClick={() => { logout(); setMobileOpen(false); }} className="text-left px-3 py-2 rounded-lg hover:bg-slate-50 text-slate-500">{t("nav.logout")}</button>
            </>
          ) : (
            <div className="flex gap-2 pt-2 border-t border-slate-100 mt-2">
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                data-testid="navbar-mobile-login"
                className="flex-1 text-center px-3 py-2 text-sm font-semibold rounded-xl border border-slate-300 text-slate-700 hover:border-[#E11D48] hover:text-[#E11D48] transition"
              >
                {t("nav.login")}
              </Link>
              <Link
                to="/register"
                onClick={() => setMobileOpen(false)}
                data-testid="navbar-mobile-register"
                className="flex-1 btn-primary text-sm text-center"
              >
                {t("nav.signup")}
              </Link>
            </div>
          )}
          <form onSubmit={submitSearch} className="mt-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Charizard, Pikachu…" className="w-full pl-9 pr-3 py-2 text-sm rounded-xl" />
          </form>
          <div className="pt-3 mt-2 border-t border-slate-100 flex flex-wrap items-center gap-3">
            <LanguageSwitcher />
            <CurrencySwitcher />
          </div>
        </nav>
      )}
    </header>
  );
}
